import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Studies() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStudy, setSelectedStudy] = useState<number | null>(null);
  const navigate = useNavigate();

  const categories = [
    { id: 'all', name: 'Todos os Estudos', icon: 'ri-book-line' },
    { id: 'doctrine', name: 'Doutrina', icon: 'ri-graduation-cap-line' },
    { id: 'prophecy', name: 'Profecia', icon: 'ri-eye-line' },
    { id: 'character', name: 'Personagens', icon: 'ri-user-line' },
    { id: 'practical', name: 'Vida Prática', icon: 'ri-heart-line' },
    { id: 'history', name: 'História Bíblica', icon: 'ri-time-line' }
  ];

  const studies = [
    {
      id: 1,
      title: 'A Criação: Gênesis 1-2',
      category: 'doctrine',
      description: 'Estudo detalhado sobre a criação do mundo e do homem segundo as Escrituras',
      verses: 'Gênesis 1:1-31, 2:1-25',
      duration: '45 min',
      difficulty: 'Iniciante',
      image: 'https://public.readdy.ai/ai/img_res/5ec234d494e206900db2b01f5addbbe7.jpg',
      content: {
        introduction: 'O livro de Gênesis nos apresenta o relato da criação de forma majestosa e ordenada. Cada dia da criação revela aspectos do caráter de Deus e Seu plano perfeito para a humanidade.',
        mainPoints: [
          'Deus como Criador Soberano - "No princípio, Deus criou os céus e a terra" (Gn 1:1)',
          'A Ordem na Criação - Cada dia teve seu propósito específico',
          'O Homem como Coroa da Criação - Criado à imagem e semelhança de Deus',
          'O Descanso de Deus - O sétimo dia como modelo para nós'
        ],
        application: 'Como podemos viver reconhecendo Deus como nosso Criador? De que forma a criação nos ensina sobre o caráter de Deus?',
        questions: [
          'O que significa ser criado "à imagem de Deus"?',
          'Por que Deus descansou no sétimo dia?',
          'Como a criação revela a glória de Deus?'
        ]
      }
    },
    {
      id: 2,
      title: 'A Fé de Abraão',
      category: 'character',
      description: 'Explorando a jornada de fé do pai da fé e suas lições para nós hoje',
      verses: 'Gênesis 12:1-9, Romanos 4:16-25',
      duration: '50 min',
      difficulty: 'Intermediário',
      image: 'https://readdy.ai/api/search-image?query=Abraham%20looking%20at%20the%20stars%20in%20the%20desert%20night%2C%20ancient%20biblical%20scene%2C%20man%20in%20robes%20pointing%20to%20starry%20sky%2C%20faith%20and%20promise%2C%20spiritual%20moment%2C%20warm%20lighting&width=400&height=250&seq=abraham-study&orientation=landscape',
      content: {
        introduction: 'Abraão é conhecido como o "pai da fé" porque creu em Deus contra toda esperança. Sua jornada nos ensina sobre confiança, obediência e as promessas de Deus.',
        mainPoints: [
          'O Chamado de Deus - "Sai da tua terra..." (Gn 12:1)',
          'A Promessa da Descendência - Filhos como as estrelas do céu',
          'A Justificação pela Fé - "Creu Abraão em Deus, e isso lhe foi imputado como justiça"',
          'O Teste Supremo - O sacrifício de Isaque'
        ],
        application: 'Como podemos desenvolver uma fé como a de Abraão? Que promessas de Deus precisamos crer hoje?',
        questions: [
          'O que motivou Abraão a deixar sua terra?',
          'Como Abraão lidou com a demora das promessas?',
          'O que podemos aprender sobre obediência através de Abraão?'
        ]
      }
    },
    {
      id: 3,
      title: 'O Sermão do Monte',
      category: 'practical',
      description: 'As bem-aventuranças e os ensinamentos práticos de Jesus para a vida cristã',
      verses: 'Mateus 5:1-48, 6:1-34, 7:1-29',
      duration: '60 min',
      difficulty: 'Intermediário',
      image: 'https://readdy.ai/api/search-image?query=Jesus%20teaching%20on%20a%20mountain%20with%20disciples%20sitting%20around%2C%20biblical%20scene%2C%20peaceful%20hillside%2C%20ancient%20robes%2C%20spiritual%20teaching%20moment%2C%20warm%20golden%20light&width=400&height=250&seq=sermon-mount&orientation=landscape',
      content: {
        introduction: 'O Sermão do Monte é considerado o manifesto do Reino de Deus. Jesus apresenta os princípios fundamentais da vida cristã e como devemos viver como cidadãos do Seu Reino.',
        mainPoints: [
          'As Bem-aventuranças - O caráter do cristão',
          'Sal e Luz - Nossa influência no mundo',
          'A Lei e os Profetas - Jesus veio cumprir, não abolir',
          'Ensinamentos Práticos - Oração, jejum, ansiedade'
        ],
        application: 'Como podemos aplicar as bem-aventuranças em nossa vida diária? De que forma podemos ser sal e luz em nossa sociedade?',
        questions: [
          'O que significa ser "pobre de espírito"?',
          'Como podemos ser pacificadores?',
          'O que Jesus ensina sobre a ansiedade?'
        ]
      }
    },
    {
      id: 4,
      title: 'As Profecias do Fim dos Tempos',
      category: 'prophecy',
      description: 'Estudo sobre as profecias bíblicas relacionadas aos últimos dias',
      verses: 'Daniel 9:24-27, Mateus 24:1-51, Apocalipse 19-22',
      duration: '70 min',
      difficulty: 'Avançado',
      image: 'https://readdy.ai/api/search-image?query=Prophetic%20vision%20of%20end%20times%2C%20heavenly%20scene%20with%20clouds%2C%20divine%20light%2C%20biblical%20prophecy%20illustration%2C%20spiritual%20atmosphere%2C%20golden%20light%20rays&width=400&height=250&seq=prophecy-study&orientation=landscape',
      content: {
        introduction: 'As profecias bíblicas sobre os últimos dias nos ajudam a entender o plano de Deus para a história e nos preparam para a volta de Cristo.',
        mainPoints: [
          'As 70 Semanas de Daniel - Cronologia profética',
          'Os Sinais dos Tempos - Mateus 24',
          'A Grande Tribulação - Período de angústia',
          'A Segunda Vinda de Cristo - O Rei dos reis'
        ],
        application: 'Como devemos viver sabendo que Cristo voltará? Que esperança as profecias nos trazem?',
        questions: [
          'Quais são os sinais da volta de Cristo?',
          'O que é a Grande Tribulação?',
          'Como devemos nos preparar para a volta do Senhor?'
        ]
      }
    }
  ];

  const filteredStudies = selectedCategory === 'all' 
    ? studies 
    : studies.filter(study => study.category === selectedCategory);

  const handleLogout = () => {
    localStorage.removeItem('user');
    navigate('/login');
  };

  const selectedStudyData = studies.find(study => study.id === selectedStudy);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-lg border-b-4 border-gradient-to-r from-blue-600 to-purple-600">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                src="https://static.readdy.ai/image/8fbfc975813ef59021c9f16b313e86f9/f5810bc36c96b4c4a03e9a9aa341e507.png"
                alt="Assembleia de Deus - Missão Europa"
                className="w-20 h-20 sm:w-24 sm:h-24 lg:w-32 lg:h-32 object-contain shadow-2xl rounded-2xl border-4 border-indigo-200"
              />
              <div className="text-center flex-1">
                <h1 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent whitespace-nowrap">
                  <span style={{ fontFamily: '"Agnes", serif' }}>IGREJA AD MISSÃO EUROPA CHURCH</span>
                </h1>
                <p className="text-sm text-gray-600">Church Portugal</p>
              </div>
            </div>
            
            <nav className="flex items-center space-x-6">
              <button 
                onClick={() => navigate('/bible')}
                className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors whitespace-nowrap"
              >
                <i className="ri-book-line"></i>
                <span>Bíblia</span>
              </button>
              <button 
                onClick={() => navigate('/devotionals')}
                className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors whitespace-nowrap"
              >
                <i className="ri-heart-line"></i>
                <span>Devocionais</span>
              </button>
              <button 
                onClick={() => navigate('/studies')}
                className="flex items-center space-x-2 px-4 py-2 text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors whitespace-nowrap"
              >
                <i className="ri-graduation-cap-line"></i>
                <span>Estudos</span>
              </button>
              <button 
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors whitespace-nowrap"
              >
                <i className="ri-logout-box-line"></i>
                <span>Sair</span>
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Banner */}
      <div className="relative h-64 bg-gradient-to-r from-green-600 via-blue-600 to-purple-600 overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <img 
          src="https://readdy.ai/api/search-image?query=Ancient%20scrolls%20and%20books%20on%20wooden%20table%2C%20biblical%20study%20scene%2C%20warm%20candlelight%2C%20scholarly%20atmosphere%2C%20old%20manuscripts%2C%20spiritual%20learning%20environment&width=1200&height=300&seq=studies-banner&orientation=landscape"
          alt="Estudos Bíblicos"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white">
            <h2 className="text-4xl font-bold mb-2">Estudos Bíblicos</h2>
            <p className="text-xl opacity-90">Aprofunde-se na Palavra de Deus</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {!selectedStudy ? (
          <>
            {/* Categorias */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-6">Categorias de Estudo</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`p-4 rounded-xl transition-all duration-200 whitespace-nowrap ${
                      selectedCategory === category.id
                        ? 'bg-blue-600 text-white shadow-lg transform scale-105'
                        : 'bg-white text-gray-700 hover:bg-blue-50 hover:text-blue-600 shadow-md'
                    }`}
                  >
                    <div className="flex flex-col items-center space-y-2">
                      <i className={`${category.icon} text-2xl`}></i>
                      <span className="text-sm font-medium">{category.name}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Lista de Estudos */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredStudies.map((study) => (
                <div key={study.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                  <div className="relative h-48">
                    <img 
                      src={study.image}
                      alt={study.title}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute top-4 right-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        study.difficulty === 'Iniciante' ? 'bg-green-100 text-green-800' :
                        study.difficulty === 'Intermediário' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {study.difficulty}
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-800 mb-2">{study.title}</h3>
                    <p className="text-gray-600 mb-4 line-clamp-2">{study.description}</p>
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm text-gray-500">
                        <i className="ri-book-open-line mr-2"></i>
                        <span>{study.verses}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <i className="ri-time-line mr-2"></i>
                        <span>{study.duration}</span>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => setSelectedStudy(study.id)}
                      className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 transition-all duration-200 whitespace-nowrap"
                    >
                      Iniciar Estudo
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          /* Conteúdo do Estudo Selecionado */
          <div className="max-w-4xl mx-auto">
            <button
              onClick={() => setSelectedStudy(null)}
              className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 mb-6 whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              <span>Voltar aos Estudos</span>
            </button>

            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="relative h-64">
                <img 
                  src={selectedStudyData?.image}
                  alt={selectedStudyData?.title}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <div className="text-center text-white">
                    <h1 className="text-4xl font-bold mb-2">{selectedStudyData?.title}</h1>
                    <p className="text-xl opacity-90">{selectedStudyData?.verses}</p>
                  </div>
                </div>
              </div>

              <div className="p-8">
                {/* Introdução */}
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                    <i className="ri-information-line text-blue-600 mr-2"></i>
                    Introdução
                  </h2>
                  <p className="text-gray-700 leading-relaxed text-lg">
                    {selectedStudyData?.content.introduction}
                  </p>
                </div>

                {/* Pontos Principais */}
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                    <i className="ri-list-check text-green-600 mr-2"></i>
                    Pontos Principais
                  </h2>
                  <div className="space-y-4">
                    {selectedStudyData?.content.mainPoints.map((point, index) => (
                      <div key={index} className="flex items-start space-x-4 p-4 bg-blue-50 rounded-lg">
                        <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-semibold">
                          {index + 1}
                        </div>
                        <p className="text-gray-800 leading-relaxed">{point}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Aplicação */}
                <div className="mb-8 bg-purple-50 rounded-lg p-6">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                    <i className="ri-lightbulb-line text-purple-600 mr-2"></i>
                    Aplicação Prática
                  </h2>
                  <p className="text-gray-700 leading-relaxed text-lg">
                    {selectedStudyData?.content.application}
                  </p>
                </div>

                {/* Perguntas para Reflexão */}
                <div className="bg-green-50 rounded-lg p-6">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                    <i className="ri-question-line text-green-600 mr-2"></i>
                    Perguntas para Reflexão
                  </h2>
                  <div className="space-y-3">
                    {selectedStudyData?.content.questions.map((question, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <span className="text-green-600 font-semibold">{index + 1}.</span>
                        <p className="text-gray-700">{question}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
